/** Workflows phase 3 (M26): time, SLA, ratings and attributes. Merged into `engage.workflows`. */
export const workflowsP3 = {
  triggers: {
    visitor_unresponsive: "Visitor has been unresponsive",
    staff_unresponsive: "Staff has been unresponsive",
    note_added: "Staff adds a note",
    before_sla_breach: "Before an SLA is breached",
    sla_breached: "SLA breached",
    visitor_visits_page: "Visitor visits a page",
    visitor_event: "Visitor event from your site",
    conversation_rated: "Conversation rated",
  },
  triggerHints: {
    visitor_unresponsive: "Nudge, then close with a rating request.",
    staff_unresponsive: "Reassign or escalate when nobody replies.",
    note_added: "Background actions from internal notes.",
    before_sla_breach: "Alert the team before a target is missed.",
    sla_breached: "Escalate once a target is missed.",
    visitor_visits_page: "Proactive message after time on a matching URL.",
    visitor_event: "React to events your site sends with Sagot('track').",
    conversation_rated: "Follow up on low ratings or thank happy visitors.",
  },
  steps: {
    wait: "Wait",
    apply_sla: "Apply SLA policy",
    set_priority: "Set priority",
    ask_rating: "Ask for a rating",
    email_visitor: "Email the visitor",
    set_attribute: "Set attribute",
  },
  summaries: {
    rating: "{{scale}} · {{question}}",
    wait_minutes_one: "Wait {{count}} minute",
    wait_minutes_other: "Wait {{count}} minutes",
    wait_hours_one: "Wait {{count}} hour",
    wait_hours_other: "Wait {{count}} hours",
    wait_days_one: "Wait {{count}} day",
    wait_days_other: "Wait {{count}} days",
    waitUntilOffice: "{{wait}}, then until the office opens",
    apply_sla: "Policy: {{policy}}",
    set_priority: "Priority → {{priority}}",
    email_visitor: "Email: {{subject}}",
    set_attribute: "{{target}} = {{value}}",
  },
  exits: {
    interrupted: "Interrupted",
    rated: "Rated",
  },
  fields: {
    waitFor: "Wait for",
    waitValue: "Wait amount",
    waitUnit: "Wait unit",
    waitUnits: { minutes: "minutes", hours: "hours", days: "days" },
    waitCap: "Your plan allows waits of up to {{value}} {{unit}}.",
    waitTooLong: "This wait is longer than your plan allows ({{value}} {{unit}}). Shorten it before publishing.",
    untilOffice: "Then wait for office hours",
    untilOfficeHint: "If the wait ends while the office is closed, the run continues when it opens.",
    interruptOn: "Stop waiting when",
    interrupts: { visitor_reply: "The visitor replies", staff_reply: "A team member replies", closed: "The conversation is closed" },
    interruptHint: "With none of these, the run always waits the full time.",
    interruptedGoTo: "If interrupted, go to",
    interruptedHint: "With no path chosen, an interrupted wait ends the run.",
    slaGateTitle: "SLA policies are on the Institution plan",
    slaGateBody: "SLA policies track first-response and resolution targets and alert your team before they are missed.",
    slaUpgrade: "See plans in Billing",
    slaPolicy: "SLA policy",
    slaPolicyHint: "Its clocks start when this step runs. Manage policies in Settings → SLA policies.",
    slaLoadFailed: "We couldn't load your SLA policies.",
    slaNone: "No SLA policies yet.",
    slaOpenSettings: "Open SLA policies",
    priority: "Priority",
    priorityHint: "Staff see it in the Inbox, where conversations can be sorted and filtered by it.",
    ratingQuestion: "Question",
    ratingQuestionHint: "Shown above the stars or thumbs.",
    ratingScale: "Rating scale",
    ratingScales: { stars: "5 stars", thumbs: "Thumbs up / down" },
    askComment: "Ask for a comment",
    askCommentHint: "After rating, the visitor can add up to {{max}} characters. They can skip it.",
    emailConsent: "Sent only when we know the visitor's email and they agreed to be contacted. Otherwise the step is skipped. Each run sends it once.",
    emailSubject: "Subject",
    emailBody: "Message",
    emailBodyHint: "Bold, italic and links work.",
    attributeObject: "Set it on",
    attributeKey: "Attribute",
    attributeValue: "Value",
    attributeValueHint: "A fixed value, or a variable from the list below.",
    noAttributes: "No attributes are defined for this object yet.",
    openAttributes: "Open Attributes",
  },
  incomplete: {
    policy: "Choose an SLA policy",
    emailSubject: "Write a subject",
    emailBody: "Write the message",
    attribute: "Choose the attribute",
    attributeValue: "Set a value",
  },
  customSchedule: {
    title: "Weekly schedule",
    hint: "Times use your workspace time zone. A day without hours is closed.",
    zone: "Workspace time zone: {{zone}}",
    days: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
    closed: "Closed",
    rangeLabel: "{{day}} {{part}} (range {{n}})",
    from: "opens",
    to: "closes",
    removeRange: "Remove range {{n}} on {{day}}",
    addRange: "Add hours",
    addRangeFor: "Add hours on {{day}}",
    problems: {
      format: "Use a time like 08:00.",
      order: "The closing time must be after the opening time.",
      overlap: "This range overlaps an earlier one.",
    },
  },
  options: {
    title: "Trigger options",
    minutes: "Minutes without a reply",
    minutesRange: "Between 1 and {{max}} minutes (7 days).",
    minutesHint: {
      visitor_unresponsive: "Starts when the visitor has not replied this long after the last message from the assistant or your team.",
      staff_unresponsive: "Starts when nobody on your team has replied this long after the visitor wrote, while a person is assigned.",
    },
    urlRules: "Pages",
    urlRulesHint: "The workflow starts on pages that match any rule. With no rule, it applies to every page.",
    ruleOperator: "Rule {{n}} operator",
    ruleValue: "Rule {{n}} URL",
    urlPlaceholder: "e.g. /admissions",
    removeRule: "Remove rule {{n}}",
    addRule: "Add a page rule",
    operators: { is: "URL is", contains: "URL contains", starts_with: "URL starts with" },
    secondsOnPage: "Seconds on the page",
    secondsOnPageHint: "How long the visitor stays before the message appears.",
    frequency: "Show until",
    frequencies: {
      until_engaged: "The visitor engages (taps a button)",
      until_interacted: "The visitor interacts at all",
      once: "Seen once",
      every_session: "Every session",
    },
    eventHint: "Add an “Event name” condition below to choose which event starts this workflow. Your site sends events with Sagot('track', 'event_name').",
  },
  conditionFields: {
    conversation_priority: "Priority",
    conversation_rating: "Rating (1–5)",
    event_name: "Event name",
    page_url: "Page URL",
    sla_breached: "SLA breached",
  },
  conditionGroups: {
    conversation: "Conversation",
    message: "Message",
    lead: "Lead",
    booking: "Booking",
    visitor: "Visitor",
    time: "Time",
    event: "Event",
    intent: "Intent",
    page: "Page",
    sla: "SLA",
  },
  attributeGroups: {
    visitor: "Visitor attributes",
    conversation: "Conversation attributes",
    lead: "Lead attributes",
  },
} as const;
